<template>
  <v-btn
    class="custom-button"
    rounded
    :block="block"
    :color="buttonColor"
    :class="{
      'px-6': !isCircleButton && !plain,
      'pa-0': plain,
      buttonOpacity: Boolean(buttonOpacity),
    }"
    :disabled="disabled"
    :elevation="elevation"
    :fab="fab"
    :href="href"
    :icon="Boolean(icon) && !fab && !text"
    :id="id"
    :large="size === SIZES.large"
    :loading="loading"
    :outlined="outlined"
    :plain="plain"
    :ripple="!plain"
    :small="size === SIZES.small"
    :target="target"
    :to="to"
    :x-large="size === SIZES['x-large']"
    :x-small="size === SIZES['x-small']"
    @mouseleave="$emit('mouseleave', $event)"
    @mouseenter="$emit('mouseenter', $event)"
    @click="$emit('click', $event)"
  >
    <v-icon
      v-if="icon && iconPosition === ICON_POSITIONS.left"
      class="mx-1"
      :color="textColor"
      :small="size === SIZES.small"
      :x-large="size === SIZES['x-large']"
      :x-small="size === SIZES['x-small']"
    >
      {{ icon }}
    </v-icon>
    <span
      class="button-text"
      :class="{ 'text-truncate': truncate }"
      :style="{ 'max-width': maxWidth && maxWidth + 'px' }"
      :color="textColor"
    >
      {{ text }}
    </span>
    <v-icon
      v-if="icon && iconPosition === ICON_POSITIONS.right"
      class="mx-1"
      :color="textColor"
    >
      {{ icon }}
    </v-icon>
  </v-btn>
</template>

<script>
export const COLORS = Object.freeze({
  primary: {
    bg: 'primary',
    text: 'white',
  },
  secondary: {
    bg: 'secondary',
    text: 'white',
  },
  grey: {
    bg: 'blue-grey darken-1',
    text: 'blue-grey darken-3',
  },
  danger: {
    bg: 'red',
    text: 'white',
  },
  warning: {
    bg: 'yellow',
    text: 'white',
  },
  white: {
    bg: 'white',
    text: 'blue-grey darken-1',
  },
  black: {
    bg: 'black',
    text: 'white',
  },
  default: {
    bg: 'primary',
    text: 'white',
  },
  blueGrey: {
    bg: 'blue-grey',
    text: 'blue-grey',
  },
})

export const ICON_POSITIONS = Object.freeze({
  left: 'left',
  right: 'right',
  default: 'left',
})

export const SIZES = Object.freeze({
  'x-small': 'x-small',
  small: 'small',
  large: 'large',
  'x-large': 'x-large',
})

export default {
  name: 'Button',

  props: {
    id: {
      type: String,
      required: false,
    },
    text: {
      type: String,
      required: false,
    },
    truncate: {
      type: Boolean,
      required: false,
    },
    maxWidth: {
      type: Number,
      required: false,
    },
    color: {
      type: String,
      required: false,
      default: () => 'default',
    },
    icon: {
      type: String,
      required: false,
    },
    elevation: {
      type: String,
      required: false,
      default: '0',
    },
    iconPosition: {
      type: String,
      required: false,
      default: 'left',
    },
    size: {
      type: String,
      required: false,
      validator: size => {
        return !size || Boolean(SIZES[size])
      },
    },
    to: {
      type: [String, Object],
      required: false,
    },
    buttonOpacity: {
      type: Number,
      required: false,
      default: 0,
    },
    loading: Boolean,
    disabled: Boolean,
    fab: Boolean,
    plain: Boolean,
    outlined: Boolean,
    block: Boolean,
    href: String,
    target: String,
  },

  data() {
    return {
      ICON_POSITIONS,
      SIZES,
    }
  },

  computed: {
    buttonColor() {
      return COLORS[this.color]?.bg ?? COLORS.default.bg
    },

    isCircleButton() {
      return this.fab || (this.icon && !this.text)
    },

    textColor() {
      const isInverted = this.plain || this.outlined
      const attr = isInverted ? 'bg' : 'text'

      return COLORS[this.color]?.[attr] ?? COLORS.default[attr]
    },
  },
}
</script>

<style lang="css" scoped>
.buttonOpacity.v-btn:before {
  opacity: 1;
}
.custom-button.v-btn--disabled.v-btn--has-bg {
  background-color: #cbd5e0 !important;
  opacity: 0.3;
  color: white !important;
}
.custom-button.v-btn--disabled.v-btn--outlined {
  border: 1px solid #cbd5e0;
  opacity: 0.4;
  color: #cbd5e0 !important;
}
.custom-button.v-btn--disabled.v-btn--plain {
  opacity: 0.7;
  color: #cbd5e0 !important;
}
.custom-button.v-btn--plain.blue-grey--text span.button-text i {
  color: #4a5568 !important;
}
.v-btn {
  text-transform: capitalize;
}
</style>
